import { Ability } from '@casl/ability';
import {
  Actions,
  MultiSelectOption,
  ResourceForm,
  Subjects,
} from '@dispatch/shared/types';
import {
  CheckBoxField,
  RadioField,
  Section,
  SelectField,
  TextField,
} from '@dispatch/web/ui';
import { useZipCodeToRegion } from '@dispatch/shared/hooks';
import { useCurrentUser } from '@dispatch/web/auth';
import { Grid, Typography, TextField as MUITextField } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormWatch,
  useController,
  Controller,
} from 'react-hook-form';
import { red } from '@mui/material/colors';

export type LocationInformationProps = {
  control: Control<FieldValues>;
  index: number;
  permissions: Ability<[Actions, Subjects]> | undefined;
  errors: FieldErrors;
  statesOptions: MultiSelectOption[];
  disableStartLocation: boolean;
  watch: UseFormWatch<ResourceForm>;
  isReadOnly: boolean;
};

export const LocationInformation = ({
  control,
  index,
  permissions,
  errors,
  statesOptions,
  disableStartLocation,
  watch,
  isReadOnly = false,
}: LocationInformationProps) => {
  const hasUpsertPermissions = permissions?.can?.('upsert', 'resource');
  const { tenantId } = useCurrentUser();
  const isVirtualLocation = watch(
    `locations.${index}.locationInformation.isVirtualLocation`
  );

  const { field: isStartingLocationField } = useController({
    name: `locations.${index}.serviceInformation.isStartingLocation`,
    control,
  });

  const [zipCodeError, setZipCodeError] = useState<string | null>(null);
  const [stateFieldError, setStateFieldError] = useState<string | null>(null);
  const [shouldDisableState, setShouldDisableState] = useState(true);

  const { field: zipField } = useController({
    name: `locations.${index}.locationInformation.zip`,
    control,
  });

  const { field: stateField } = useController({
    name: `locations.${index}.locationInformation.state`,
    control,
  });

  const { field: countyField } = useController({
    name: `locations.${index}.locationInformation.county`,
    control,
  });

  const handleStateChange = useCallback(
    (state: string | null) => {
      if (state) {
        stateField.onChange(state);
        setStateFieldError(null);
        setShouldDisableState(true);
      } else {
        stateField.onChange('');
        setStateFieldError(null);
        setShouldDisableState(true);
      }
    },
    [stateField]
  );

  const handleCountyChange = useCallback(
    (county: string | null) => {
      countyField.onChange(county ?? '');
    },
    [countyField]
  );

  const handleErrorChange = useCallback((error: string | null) => {
    setZipCodeError(error);
    setStateFieldError(null);
    setShouldDisableState(true);
  }, []);

  const handleLoadingChange = useCallback((loading: boolean) => {
    if (loading) {
      setShouldDisableState(true);
    }
  }, []);

  // Initialize the zip code to region hook
  const { handleZipCodeChange, formatZipCode, resetLastValidZip } =
    useZipCodeToRegion({
      onStateChange: handleStateChange,
      onCountyChange: handleCountyChange,
      onErrorChange: handleErrorChange,
      onLoadingChange: handleLoadingChange,
      tenantId: tenantId || '',
    });

  // Reset lastValidZip when component mounts to allow API calls for existing zip codes
  useEffect(() => {
    resetLastValidZip();
  }, [resetLastValidZip]);

  // Clear errors when zip code is cleared
  useEffect(() => {
    if (!zipField.value) {
      setZipCodeError(null);
      setStateFieldError(null);
      setShouldDisableState(true);
    }
  }, [zipField.value]);

  // Clear isStartingLocation when switching to virtual location
  useEffect(() => {
    if (isVirtualLocation === 'true' && isStartingLocationField.value) {
      isStartingLocationField.onChange(false);
    }
  }, [isVirtualLocation, isStartingLocationField]);

  return (
    <Section title="Location Information">
      <Grid container alignItems="center" spacing={2}>
        <Grid size={12}>
          <TextField
            id={`name`}
            name={`locations.${index}.locationInformation.name`}
            label="Location Name"
            type="text"
            control={control}
            inputProps={{
              readOnly: !hasUpsertPermissions,
            }}
            errors={
              errors && errors['locationInformation']
                ? errors['locationInformation']
                : null
            }
            required
            disabled={isReadOnly}
          />
        </Grid>
      </Grid>
      <Grid container alignItems="center" spacing={2}>
        <Grid size={9}>
          {!JSON.parse(isVirtualLocation ?? 'true') && (
            <TextField
              id={`street`}
              name={`locations.${index}.locationInformation.street`}
              label="Street"
              type="text"
              control={control}
              inputProps={{
                readOnly: !hasUpsertPermissions,
              }}
              errors={
                errors && errors['locationInformation']
                  ? errors['locationInformation']
                  : null
              }
              disabled={isReadOnly}
            />
          )}
        </Grid>
        <Grid size={3}>
          {!JSON.parse(isVirtualLocation ?? 'true') && (
            <TextField
              id={`unit`}
              name={`locations.${index}.locationInformation.unit`}
              label="Unit"
              type="text"
              control={control}
              inputProps={{
                readOnly: !hasUpsertPermissions,
              }}
              errors={
                errors && errors['locationInformation']
                  ? errors['locationInformation']
                  : null
              }
              disabled={isReadOnly}
            />
          )}
        </Grid>
      </Grid>
      <Grid container alignItems="center">
        <Grid size={12}>
          {!JSON.parse(isVirtualLocation ?? 'true') && (
            <TextField
              id={`city`}
              name={`locations.${index}.locationInformation.city`}
              label="City"
              type="text"
              control={control}
              inputProps={{
                readOnly: !hasUpsertPermissions,
              }}
              errors={
                errors && errors['locationInformation']
                  ? errors['locationInformation']
                  : null
              }
              disabled={isReadOnly}
            />
          )}
        </Grid>
      </Grid>
      <Grid container alignItems="center" spacing={2}>
        <Grid size={6}>
          <SelectField
            id={`state`}
            name={`locations.${index}.locationInformation.state`}
            label="State"
            control={control}
            readOnly={!hasUpsertPermissions}
            errors={
              errors && errors['locationInformation']
                ? errors['locationInformation']
                : null
            }
            required
            options={statesOptions}
            disabled={isReadOnly || shouldDisableState}
          />
          {stateFieldError && (
            <Typography
              variant="caption"
              sx={{
                color: red[600],
                fontSize: '0.75rem',
                marginTop: '4px',
                display: 'block',
              }}
            >
              {stateFieldError}
            </Typography>
          )}
        </Grid>
        <Grid size={6}>
          <Controller
            name={`locations.${index}.locationInformation.zip`}
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <MUITextField
                {...field}
                id={`zip`}
                label="Zip Code"
                type="text"
                sx={{ width: '100%' }}
                variant="standard"
                inputProps={{
                  readOnly: !hasUpsertPermissions,
                  maxLength: 10, // 5 digits + dash + 4 digits
                }}
                onChange={(event) => {
                  const value = event.target.value;
                  const formattedValue = formatZipCode(value);

                  // Update the form field
                  field.onChange(formattedValue);

                  // Trigger state lookup if valid format
                  handleZipCodeChange(formattedValue);
                }}
                error={
                  (errors &&
                    errors['locationInformation'] &&
                    errors['locationInformation']['zip']) ||
                  !!zipCodeError
                }
                helperText={
                  (errors &&
                    errors['locationInformation'] &&
                    errors['locationInformation']['zip']?.message) ||
                  zipCodeError
                }
                required
                disabled={isReadOnly}
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            id={`county`}
            name={`locations.${index}.locationInformation.county`}
            label="County"
            type="text"
            control={control}
            disabled
          />
        </Grid>
        <Grid container justifyContent={'flex-end'} spacing={0} size={12}>
          <RadioField
            row
            control={control}
            id={`isVirtualLocation`}
            name={`locations.${index}.locationInformation.isVirtualLocation`}
            // radio field requires a string value in order for watch form to work properly
            // TODO: RadioField should be updated to handle unknown values properly
            buttons={[
              {
                label: 'Virtual',
                value: 'true',
              },
              {
                label: 'Field',
                value: 'false',
              },
            ]}
            disabled={!hasUpsertPermissions || isReadOnly}
          />
        </Grid>
        <Grid
          container
          justifyContent="flex-end"
          spacing={1}
          size={12}
          sx={{ mt: -1 }}
        >
          {!JSON.parse(isVirtualLocation ?? 'true') && (
            <CheckBoxField
              control={control}
              id={'isStartingLocation'}
              name={`locations.${index}.serviceInformation.isStartingLocation`}
              label={'Starting Location'}
              disabled={disableStartLocation || isReadOnly}
            />
          )}
        </Grid>
      </Grid>
    </Section>
  );
};
